import { KeyMapKeys } from '@locales/lokalise/keyMap';
import { ENUM_CONSTANTS } from 'components/shared/enum.constants';
import { CAPTCHA_PROVIDERS, ENUM_LOCALE_CODE, LANGUAGE_CODE_TO_LOCALE_CODE } from 'global.enums';
import { LanguageType } from 'global.interfaces';
import i18next from 'i18next';
import { IEnumConstant } from './enum.constants.interface';
import { EmptyObject } from './shared.interfaces';

export interface ILocaleStructure {
  key: ENUM_LOCALE_CODE;
  title: string;
  i18n: string;
}

export interface ILocalesEnum {
  name: string;
  value: ENUM_LOCALE_CODE;
}

interface IExcludeOptions {
  exclude?: boolean;
  ignoreValue?: string | null;
}

export type LocalMapType = {
  [key in LanguageType]: ILocaleStructure;
};

const SETTINGS = window.settings as ISystemSettings;

export class GENERAL_CONSTANTS {
  static get LOCALES(): LocalMapType | EmptyObject {
    if (!SETTINGS || !SETTINGS.language || SETTINGS.language.length === 0) {
      return {};
    }

    const localeMap: LocalMapType = SETTINGS.language
      .sort((a, b) => a.order - b.order)
      .filter((lang) => !lang.exclude)
      .reduce<LocalMapType>((acc, lang) => {
        const key = lang.code.replace('-', '_').toLocaleUpperCase() as LanguageType;

        const i18nTranslationKey = `enum.language.${lang.key.toLowerCase()}`;

        acc[key] = {
          key: lang.code as ENUM_LOCALE_CODE,
          title: i18next.t(i18nTranslationKey as KeyMapKeys),
          i18n: i18nTranslationKey,
        };

        return acc;
      }, {} as LocalMapType);

    return localeMap;
  }

  static get DEFAULT_LOCALE(): ENUM_LOCALE_CODE {
    const locales = this.LOCALES;

    const localesKeys = Object.keys(locales);

    if (localesKeys.length == 0) {
      return LANGUAGE_CODE_TO_LOCALE_CODE.en;
    }

    return locales[localesKeys[0] as LanguageType].key;
  }

  static get LOCALES_ENUM(): ILocalesEnum[] {
    const locales = this.LOCALES;

    return Object.keys(locales).map((lang) => ({
      name: locales[lang as LanguageType].i18n,
      value: locales[lang as LanguageType].key,
    }));
  }

  static get COUNTRY(): IEnumConstant[] {
    return this.getCountryList();
  }

  static get COUNTRY_WITH_EXCLUDED(): IEnumConstant[] {
    return this.getCountryList({ exclude: false });
  }

  static get COUNTRY_WITH_ALL_NOT_ALLOWED_AND_EXCLUDED(): IEnumConstant[] {
    return this.getGeneralEnumList('country', 'COUNTRY', { exclude: false });
  }

  static get COUNTRY_WITH_ALL_AND_EXCLUDED(): IEnumConstant[] {
    return this.getGeneralEnumList('country', 'COUNTRY', { exclude: false }).filter(
      (c) => c.name !== 'enum.country.notAllowed',
    );
  }

  static get DEFAULT_COUNTRY(): string | undefined {
    const countries = this.PREFERRED_COUNTRIES;

    return countries.length > 0 ? countries[0] : undefined;
  }

  static get PREFERRED_COUNTRIES(): string[] {
    if (!SETTINGS || !SETTINGS.country) {
      return [];
    }

    return SETTINGS.country
      .filter((x) => x.preferred)
      .sort((a, b) => a.order - b.order)
      .map((x) => x.code.toLowerCase());
  }

  static get EXCLUDE_COUNTRIES(): string[] {
    if (!SETTINGS || !SETTINGS.country) {
      return [];
    }

    return SETTINGS.country.filter((x) => x.exclude).map((x) => x.code.toLowerCase());
  }

  static get PSP_CURRENCY(): IEnumConstant[] {
    return this.getGeneralEnumList('currency', 'PSP_CURRENCY');
  }

  static get DEFAULT_CURRENCY(): string {
    const currency = this.PSP_CURRENCY;

    if (currency.length > 0) {
      return currency[0].value;
    }

    return '';
  }

  static get WALLET_OPERATION_CATEGORY(): IEnumConstant[] {
    return this.getGeneralEnumList('walletOperationCategory', 'WALLET_OPERATION_CATEGORY');
  }

  static get GROUP_MAPPING(): IEnumConstant[] {
    return this.getGeneralEnumList('groupMapping', 'GROUP_MAPPING');
  }

  static get DEFAULT_NUMBER_PRECISION(): number {
    if (!SETTINGS?.numberPrecision) {
      return 4;
    }

    return SETTINGS.numberPrecision;
  }

  static get ID_HASH_SUFFIX(): string {
    if (!SETTINGS?.gtmSettings?.idHashSuffix) {
      return '';
    }

    return SETTINGS.gtmSettings.idHashSuffix;
  }

  static get CAPTCHA_PROVIDER(): CAPTCHA_PROVIDERS | null {
    if (!SETTINGS?.captchaSettings?.provider) {
      return null;
    }

    return SETTINGS.captchaSettings.provider.toLowerCase() === CAPTCHA_PROVIDERS.HCAPTCHA
      ? CAPTCHA_PROVIDERS.HCAPTCHA
      : CAPTCHA_PROVIDERS.RECAPTCHA;
  }

  static get CAPTCHA_SETTINGS_KEY(): string {
    if (!SETTINGS?.captchaSettings?.siteKey) {
      return '';
    }

    return SETTINGS.captchaSettings.siteKey;
  }

  static get CAPTCHA_SIGNIN_PROTECTION(): boolean {
    if (!SETTINGS?.captchaSettings?.signInProtection) {
      return false;
    }

    return SETTINGS.captchaSettings.signInProtection;
  }

  static get CAPTCHA_WIDGET_SETTINGS() {
    if (!SETTINGS?.captchaSettings?.widgetSettings) {
      return {};
    }

    return SETTINGS.captchaSettings.widgetSettings;
  }

  static get MEASUREMENT_SETTINGS(): ISystemMeasurementSetting[] {
    if (!SETTINGS?.measurementSettings) {
      return [];
    }

    return SETTINGS.measurementSettings;
  }

  static getGeneralEnumList(
    globalProperty: keyof ISystemSettings,
    enumName: keyof typeof ENUM_CONSTANTS,
    excludeOptions?: IExcludeOptions,
  ): IEnumConstant[] {
    if (!SETTINGS) {
      return [];
    }

    const list = SETTINGS[globalProperty];

    if (!Array.isArray(list)) {
      return [];
    }

    const fullEnumList = ENUM_CONSTANTS[enumName];
    const resultList: Array<IEnumConstant> = [];
    const exclude = excludeOptions?.exclude ?? true;

    list
      .sort((a, b) => a.order - b.order)
      .forEach((item) => {
        if (exclude && item.exclude && excludeOptions?.ignoreValue !== item.key) {
          return;
        }

        // @ts-ignore
        const it = fullEnumList.find((x) => x.value === item.key);

        if (it) {
          resultList.push(it);
        }
      });

    return resultList;
  }

  static getCountryList(excludeOptions?: IExcludeOptions): IEnumConstant[] {
    return this.getGeneralEnumList('country', 'COUNTRY', excludeOptions).filter(
      (c) => c.name !== 'enum.country.notAllowed' && c.name !== 'enum.country.allCountries',
    );
  }

  static getSystemSettingsCountryByKey(key: string): ISystemSettingsCountry | undefined {
    return SETTINGS.country.find((x) => x.key === key);
  }
}
